import { getAudioContext } from './AudioEngine';

const BASE_URL = process.env.REACT_APP_PRODUCER_SOUNDS_URL || '/producer-sounds';
let manifestPromise = null;
let manifest = { version: 0, drumKits: [], oneShots: [], oneShotCategories: [] };
const buffers = new Map();
const pending = new Map();

export function parseRootNote(value) {
  if (Number.isInteger(Number(value))) return Math.max(0, Math.min(127, Number(value)));
  const match = /^([A-Ga-g])([#b]?)(-?\d+)$/.exec(String(value || '').trim());
  if (!match) return 60;
  const semitones = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let note = semitones[match[1].toUpperCase()];
  if (match[2] === '#') note += 1;
  if (match[2] === 'b') note -= 1;
  // Producer manifests label middle C as C3.
  return Math.max(0, Math.min(127, (Number(match[3]) + 2) * 12 + note));
}

export function filterManifest(source, { production = process.env.NODE_ENV === 'production', allowUncleared = process.env.REACT_APP_ALLOW_UNCLEARED === '1' } = {}) {
  const shouldFilter = production && !allowUncleared;
  const usable = (entry) => !shouldFilter || entry?.cleared === true;
  const drumKits = (Array.isArray(source?.drumKits) ? source.drumKits : []).map((kit) => ({
    ...kit,
    rows: (Array.isArray(kit.rows) ? kit.rows : []).map((row) => ({
      ...row,
      sounds: (Array.isArray(row.sounds) ? row.sounds : []).filter(usable),
    })).filter((row) => row.sounds.length > 0),
  })).filter((kit) => kit.rows.length > 0);
  const oneShots = (Array.isArray(source?.oneShots) ? source.oneShots : []).filter(usable).map((sound) => ({
    ...sound,
    rootNote: parseRootNote(sound.rootNote),
  }));
  const listedCategories = (Array.isArray(source?.oneShotCategories) ? source.oneShotCategories : [])
    .map((category) => typeof category === 'string'
      ? { id: category, label: category }
      : { id: String(category?.id || ''), label: String(category?.label || category?.id || '') })
    .filter((category) => category.id);
  const fallbackCategories = [...new Set(oneShots.map((sound) => sound.category).filter(Boolean))]
    .map((category) => ({ id: category, label: category }));
  return { version: source?.version ?? 0, drumKits, oneShots, oneShotCategories: listedCategories.length ? listedCategories : fallbackCategories };
}

export async function loadProducerSounds() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${BASE_URL}/manifest.json`).then((response) => {
      if (!response.ok) throw new Error(`Producer sounds manifest failed: ${response.status}`);
      return response.json();
    }).then((source) => {
      manifest = filterManifest(source);
      return manifest;
    }).catch((error) => {
      console.warn('Producer sounds unavailable:', error.message);
      manifest = { version: 0, drumKits: [], oneShots: [], oneShotCategories: [] };
      return manifest;
    });
  }
  return manifestPromise;
}

export function getManifest() { return manifest; }
export function getKits() { return manifest.drumKits || []; }
export function getOneShots(category) {
  return (manifest.oneShots || []).filter((sound) => !category || sound.category === category);
}
export function getOneShotCategories() { return manifest.oneShotCategories || []; }

export function findSound(id) {
  for (const kit of manifest.drumKits || []) {
    for (const row of kit.rows || []) {
      const sound = (row.sounds || []).find((candidate) => candidate.id === id);
      if (sound) return sound;
    }
  }
  return (manifest.oneShots || []).find((sound) => sound.id === id) || null;
}

export async function getSoundBuffer(id) {
  if (!id) return null;
  await loadProducerSounds();
  if (buffers.has(id)) return buffers.get(id);
  if (pending.has(id)) return pending.get(id);
  const sound = findSound(id);
  if (!sound?.file) return null;
  const promise = fetch(`${BASE_URL}/${String(sound.file).replace(/^\//, '')}`).then((response) => {
    if (!response.ok) throw new Error(`Producer sound failed: ${response.status}`);
    return response.arrayBuffer();
  }).then((data) => getAudioContext().decodeAudioData(data)).then((buffer) => {
    buffers.set(id, buffer);
    return buffer;
  }).finally(() => pending.delete(id));
  pending.set(id, promise);
  return promise;
}

export function soundSignature(id) {
  const sound = findSound(id);
  return sound ? [sound.id, sound.file] : [id, null];
}

/** Test-only data seam: synthetic manifests and generated AudioBuffers need no network. */
export function __setProducerSoundsForTests(source, soundBuffers = {}) {
  manifest = filterManifest(source, { production: false, allowUncleared: true });
  manifestPromise = Promise.resolve(manifest);
  buffers.clear();
  pending.clear();
  for (const [id, buffer] of Object.entries(soundBuffers)) buffers.set(id, buffer);
}

export function __resetProducerSoundsForTests() {
  manifestPromise = null;
  manifest = { version: 0, drumKits: [], oneShots: [], oneShotCategories: [] };
  buffers.clear();
  pending.clear();
}
